import { RangeSlider } from '@warp-ds/components/react/slider';
import { TextField } from '@warp-ds/react/components/textfield';
import { memo, useEffect, useRef, useState } from 'react';

type RangeSliderFilterProps = {
    id: string;
    title: string;
    label: [string, string];
    value: [number, number];
    min: number;
    max: number;
    step?: number;
    onChange: (value: [number, number]) => void;
    renderValue?: (value: number) => string;
    className?: string;
    disabled?: boolean;
    showMarks?: boolean;
    showInputs?: boolean;
};

export const RangeSliderFilter = memo(
    ({
        id,
        title,
        label,
        value,
        min,
        max,
        step = 1,
        onChange,
        renderValue,
        className,
        disabled = false,
        showMarks = true,
        showInputs = true,
    }: RangeSliderFilterProps) => {
        const [displayValue, setDisplayValue] = useState<[number, number]>(value);
        const valueChangeTimeout = useRef<NodeJS.Timeout | null>(null);

        useEffect(() => {
            setDisplayValue(value);
        }, [value]);

        const handleChange = (newValue: [number, number]): void => {
            setDisplayValue(newValue);
            onChange(newValue);
        };

        const handleTextChange = (newValue: [number, number]): void => {
            setDisplayValue(newValue);

            const newMin = Math.min(Math.max(newValue[0], min), Math.max(newValue[1], min));
            const newMax = Math.min(Math.max(newValue[1], newValue[0]), max);

            if (valueChangeTimeout.current) {
                clearTimeout(valueChangeTimeout.current);
            }
            valueChangeTimeout.current = setTimeout((): void => {
                setDisplayValue([newMin, newMax]);
                onChange([newMin, newMax]);
            }, 800);
        };

        const valueText: [string, string] = renderValue
            ? [renderValue(displayValue[0]), renderValue(displayValue[1])]
            : [displayValue[0].toString(), displayValue[1].toString()];

        return (
            <div data-testid={id} className={className}>
                <RangeSlider
                    label={title}
                    onInput={setDisplayValue}
                    onChange={handleChange}
                    min={min}
                    max={max}
                    step={step}
                    value={displayValue}
                    aria-label={label}
                    aria-valuetext={valueText}
                    disabled={disabled}
                    showMarks={showMarks}
                />
                {showInputs ? (
                    <div className="w-slider__input">
                        <TextField
                            type="number"
                            min={min}
                            max={max}
                            step={step}
                            helpText={label[0]}
                            value={displayValue[0].toString()}
                            onChange={(e) => handleTextChange([~~e.target.value, displayValue[1]])}
                            onFocus={(e) => e.target.select()}
                            disabled={disabled}
                        />
                        <TextField
                            type="number"
                            min={min}
                            max={max}
                            step={step}
                            helpText={label[1]}
                            value={displayValue[1].toString()}
                            onChange={(e) => handleTextChange([displayValue[0], ~~e.target.value])}
                            onFocus={(e) => e.target.select()}
                            disabled={disabled}
                        />
                    </div>
                ) : (
                    <div>
                        {valueText[0]} - {valueText[1]}
                    </div>
                )}
            </div>
        );
    },
);

RangeSliderFilter.displayName = 'RangeSliderFilter';
